<script setup lang="ts">
import { Select as VarSelect } from '@varlet/ui';
import { computed, h, type VNode } from 'vue';

import { AppLocale, translate, useLocale } from '@/shared/i18n';

import { Icon, type IconName } from './icon';

const { locale, localeOptions } = useLocale();

type LanguageOption = {
  label: VNode;
  value: AppLocale;
};

const localeFlags: Readonly<Record<AppLocale, IconName>> = {
  [AppLocale.RU]: 'circle-flags:ru',
  [AppLocale.EN]: 'circle-flags:us',
} as const;

const languageOptions = computed<LanguageOption[]>(() =>
  localeOptions.map((localeOption: AppLocale): LanguageOption => ({
    label: h('span', { class: 'flex items-center gap-2' }, [
      h(Icon, { name: localeFlags[localeOption], class: 'h-5 w-5 shrink-0' }),
      translate(`language.${localeOption}`),
    ]),
    value: localeOption,
  })),
);
</script>

<template>
  <div class="flex items-center gap-2">
    <Icon
      name="material-symbols:translate"
      width="20"
      class="mr-2 text-[var(--theme-text-primary)]"
    />

    <Icon
      :name="localeFlags[locale]"
      class="mr-2 h-5 w-5 shrink-0"
    />

    <VarSelect
      v-model="locale"
      class="rounded-xl focus-visible:outline-2! focus-visible:outline-offset-2! focus-visible:outline-[var(--theme-focus)]!"
      :label="translate('common.language')"
      :options="languageOptions"
      variant="outlined"
      size="small"
    >
      <template #selected>
        <span class="flex flex-nowrap items-center gap-2 text-nowrap">
          {{ translate(`language.${locale}`) }}
        </span>
      </template>

      <template #prepend-icon> </template>
    </VarSelect>
  </div>
</template>
